import React, { useEffect, useState, useCallback, useRef } from "react";
import { Trash2, Plus, Upload, Sparkles, Move, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { cropStyle } from "../../lib/cropStyle";
import { FocalAdjuster } from "./common";

export default function MediaAdmin() {
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("All");
  const [newCat, setNewCat] = useState("");
  const [newCatParent, setNewCatParent] = useState("");
  const [uploadCat, setUploadCat] = useState("Gallery");
  const [uploading, setUploading] = useState(0);
  const [prog, setProg] = useState({ done: 0, total: 0, failed: 0 });
  const [urlForm, setUrlForm] = useState({ url: "", title: "" });
  const [adjusting, setAdjusting] = useState(null);

  const loadMedia = useCallback(() => api.get("/admin/media").then((r) => setList(r.data)).catch(() => {}), []);
  const loadCats = useCallback(() => api.get("/media-categories").then((r) => {
    setCats(r.data); setUploadCat((c) => (c === "Gallery" && r.data[0]) ? r.data[0].name : c);
  }).catch(() => {}), []);
  useEffect(() => { loadMedia(); loadCats(); }, [loadMedia, loadCats]);

  const addCat = async () => {
    if (!newCat.trim()) return;
    try { await api.post("/admin/media-categories", { name: newCat.trim(), parent: newCatParent || undefined }); setNewCat(""); setNewCatParent(""); loadCats(); toast.success("Category added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const renameCat = async (c) => {
    const name = window.prompt("Rename category", c.name);
    if (!name || name.trim() === c.name) return;
    await api.patch(`/admin/media-categories/${c.id}`, { name: name.trim() }); loadCats(); loadMedia(); toast.success("Renamed");
  };
  const delCat = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Photos in it move to Gallery.`)) return;
    await api.delete(`/admin/media-categories/${c.id}`); loadCats(); loadMedia();
  };

  const bulkUpload = async (files) => {
    const arr = Array.from(files);
    setUploading(arr.length);
    setProg({ done: 0, total: arr.length, failed: 0 });
    let idx = 0, done = 0, failed = 0;
    const CONC = Math.min(4, arr.length);
    const worker = async () => {
      while (idx < arr.length) {
        const f = arr[idx++];
        const fd = new FormData();
        fd.append("file", f); fd.append("album", uploadCat); fd.append("title", f.name.replace(/\.[^.]+$/, ""));
        try { await api.post("/admin/media/upload", fd); } catch { failed += 1; }
        done += 1; setProg({ done, total: arr.length, failed });
      }
    };
    await Promise.all(Array.from({ length: CONC }, worker));
    setUploading(0); loadMedia();
    toast[failed ? "warning" : "success"](`Uploaded ${arr.length - failed}/${arr.length}${failed ? `, ${failed} failed` : ""}`);
  };

  const addByUrl = async () => {
    if (!urlForm.url) { toast.error("Provide an image URL"); return; }
    try { await api.post("/admin/media", { url: urlForm.url, album: uploadCat, title: urlForm.title, type: "image", featured: false }); setUrlForm({ url: "", title: "" }); loadMedia(); toast.success("Added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const patchMedia = async (id, patch) => { await api.patch(`/admin/media/${id}`, patch); loadMedia(); };
  const del = async (id) => { await api.delete(`/admin/media/${id}`); loadMedia(); };
  const dragMedia = useRef(null);
  const reorder = async (fromId, toId) => {
    const arr = [...list];
    const fi = arr.findIndex((m) => m.id === fromId), ti = arr.findIndex((m) => m.id === toId);
    if (fi < 0 || ti < 0 || fi === ti) return;
    const [it] = arr.splice(fi, 1); arr.splice(ti, 0, it);
    setList(arr);
    try { await api.post("/admin/media/reorder", { ids: arr.map((m) => m.id) }); } catch { loadMedia(); }
  };

  const filtered = filter === "All" ? list : list.filter((m) => m.album === filter);

  return (
    <div className="space-y-6">
      <div className="card-lux p-6" data-testid="media-categories">
        <h3 className="font-serif text-xl text-forest mb-1">Gallery categories</h3>
        <p className="text-xs text-stone-500 mb-4">Create categories to organise photos — these become the filters guests see on the Gallery page.</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {cats.map((c) => (
            <span key={c.id} data-testid={`cat-chip-${c.id}`} className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm ${c.parent ? "bg-forest/10 text-forest ml-2" : "bg-sand text-forest"}`}>
              {c.parent && <span className="text-stone-400 text-xs">↳</span>}{c.name}{c.parent && <span className="text-[10px] text-stone-400">under {c.parent}</span>}
              <button onClick={() => renameCat(c)} data-testid={`cat-rename-${c.id}`} className="text-stone-400 hover:text-rose" title="Rename">✎</button>
              <button onClick={() => delCat(c)} data-testid={`cat-delete-${c.id}`} className="text-stone-400 hover:text-red-600" title="Delete"><Trash2 size={13} /></button>
            </span>
          ))}
          {cats.length === 0 && <span className="text-sm text-stone-400">No categories yet.</span>}
        </div>
        <div className="flex flex-wrap gap-2 max-w-xl">
          <input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCat())} placeholder="New category name" data-testid="cat-name-input" className="flex-1 min-w-[160px] rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
          <select value={newCatParent} onChange={(e) => setNewCatParent(e.target.value)} data-testid="cat-parent-select" className="rounded-lg border border-stone-200 px-2 py-2.5 text-sm text-stone-600">
            <option value="">Top-level</option>
            {cats.filter((c) => !c.parent).map((c) => <option key={c.id} value={c.name}>Under {c.name}</option>)}
          </select>
          <button onClick={addCat} data-testid="cat-add-button" className="btn-primary"><Plus size={15} /> Add</button>
        </div>
      </div>

      <div className="card-lux p-6" data-testid="media-upload">
        <h3 className="font-serif text-xl text-forest mb-4">Upload photos</h3>
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <label className="text-sm"><span className="text-xs uppercase tracking-wide text-stone-500">Category</span>
            <select value={uploadCat} onChange={(e) => setUploadCat(e.target.value)} data-testid="upload-category" className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm">
              {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              <option value="Gallery">Gallery</option>
            </select></label>
          <label className={`btn-dark justify-center md:col-span-2 ${uploading ? "opacity-60 pointer-events-none" : "cursor-pointer"}`} data-testid="bulk-upload-button">
            <Upload size={15} /> {uploading ? `Uploading… ${prog.done}/${prog.total}` : "Select photos (bulk upload)"}
            <input type="file" accept="image/*" multiple className="hidden" disabled={uploading > 0} onChange={(e) => e.target.files.length && bulkUpload(e.target.files)} />
          </label>
        </div>
        {uploading > 0 && (
          <div className="mt-3" data-testid="upload-progress">
            <div className="h-2 w-full rounded-full bg-stone-100 overflow-hidden">
              <div className="h-full bg-rose transition-all duration-300" style={{ width: `${prog.total ? Math.round((prog.done / prog.total) * 100) : 0}%` }} />
            </div>
            <div className="text-xs text-stone-500 mt-1">{prog.done} of {prog.total} uploaded{prog.failed ? ` · ${prog.failed} failed` : ""}</div>
          </div>
        )}
        <div className="mt-4 grid md:grid-cols-3 gap-3 items-end border-t border-stone-100 pt-4">
          <label className="text-sm md:col-span-2"><span className="text-xs uppercase tracking-wide text-stone-500">…or add by image URL</span>
            <input placeholder="https://…" value={urlForm.url} onChange={(e) => setUrlForm({ ...urlForm, url: e.target.value })} data-testid="media-url-input" className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" /></label>
          <button onClick={addByUrl} data-testid="media-url-add" className="btn-primary"><Plus size={15} /> Add URL</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {["All", ...cats.map((c) => c.name)].map((a) => (
          <button key={a} onClick={() => setFilter(a)} data-testid={`media-filter-${a}`} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${filter === a ? "bg-rose text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-rose/40"}`}>{a}</button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {filtered.map((m) => (
          <div key={m.id} draggable={adjusting !== m.id} onDragStart={() => { dragMedia.current = m.id; }} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragMedia.current) { reorder(dragMedia.current, m.id); dragMedia.current = null; } }} className="card-lux overflow-hidden group relative" data-testid={`media-${m.id}`}>
            {adjusting === m.id ? (
              <FocalAdjuster url={m.url} value={m.position} zoom={m.zoom} onChange={({ position, zoom }) => patchMedia(m.id, { position, zoom })} onClose={() => setAdjusting(null)} />
            ) : (
            <>
            <img src={m.url} alt={m.title} className={`h-36 w-full object-cover transition-opacity ${m.hidden ? "opacity-40 grayscale" : ""}`} style={cropStyle(m.position, m.zoom)} />
            <div className="absolute top-2 left-2 flex gap-1">
              {m.bookable && m.price > 0 && <span data-testid={`media-badge-${m.id}`} className="text-[10px] font-semibold bg-forest text-cream px-2 py-0.5 rounded-full">${m.price}</span>}
              {m.hidden && <span className="text-[10px] font-medium bg-stone-800/80 text-white px-2 py-0.5 rounded-full">Hidden</span>}
            </div>
            <div className="p-2 space-y-2">
              <input defaultValue={m.title} onBlur={(e) => e.target.value !== (m.title || "") && patchMedia(m.id, { title: e.target.value })} placeholder="Label…" data-testid={`media-title-${m.id}`} className="w-full text-xs rounded border border-stone-200 px-2 py-1" />
              <select value={m.album} onChange={(e) => patchMedia(m.id, { album: e.target.value, subcategory: "" })} data-testid={`media-cat-${m.id}`} className="w-full text-xs rounded border border-stone-200 px-1.5 py-1">
                {Array.from(new Set([...cats.filter((c) => !c.parent).map((c) => c.name), "Gallery", m.album])).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              {cats.some((c) => c.parent === m.album) && (
                <select value={m.subcategory || ""} onChange={(e) => patchMedia(m.id, { subcategory: e.target.value })} data-testid={`media-subcat-${m.id}`} className="w-full text-xs rounded border border-stone-200 px-1.5 py-1">
                  <option value="">— subcategory —</option>
                  {cats.filter((c) => c.parent === m.album).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              )}
              <button onClick={() => patchMedia(m.id, { bookable: !m.bookable })} data-testid={`media-bookable-${m.id}`} className={`w-full flex items-center justify-center gap-1.5 text-xs rounded py-1.5 transition-colors ${m.bookable ? "bg-forest text-cream hover:bg-forest/90" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
                {m.bookable ? <><Sparkles size={13} /> Bookable</> : <><Plus size={13} /> Make bookable</>}
              </button>
              {m.bookable && (
                <>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-stone-400">$</span>
                    <input type="number" min="0" step="1" defaultValue={m.price || 0} onBlur={(e) => Number(e.target.value) !== (m.price || 0) && patchMedia(m.id, { price: Number(e.target.value) })} data-testid={`media-price-${m.id}`} placeholder="Price" className="flex-1 text-xs rounded border border-stone-200 px-2 py-1" />
                  </div>
                  <select value={m.price_basis || "per_guest"} onChange={(e) => patchMedia(m.id, { price_basis: e.target.value })} data-testid={`media-basis-${m.id}`} className="w-full text-xs rounded border border-stone-200 px-1.5 py-1">
                    <option value="per_guest">Per guest</option>
                    <option value="per_night">Per night</option>
                    <option value="one_off">One-off</option>
                  </select>
                  <input type="number" min="0" step="1" defaultValue={m.max_guests || 0} onBlur={(e) => Number(e.target.value) !== (m.max_guests || 0) && patchMedia(m.id, { max_guests: Number(e.target.value) })} data-testid={`media-maxguests-${m.id}`} placeholder="Max guests (0 = no cap)" className="w-full text-xs rounded border border-stone-200 px-2 py-1" />
                  <button onClick={() => patchMedia(m.id, { sold_out: !m.sold_out })} data-testid={`media-soldout-${m.id}`} className={`w-full flex items-center justify-center gap-1.5 text-xs rounded py-1.5 transition-colors ${m.sold_out ? "bg-red-600 text-white hover:bg-red-700" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>{m.sold_out ? "● Sold out" : "Mark sold out"}</button>
                </>
              )}
              <button onClick={() => setAdjusting(m.id)} data-testid={`media-adjust-${m.id}`} className="w-full flex items-center justify-center gap-1.5 text-xs rounded py-1.5 bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"><Move size={13} /> Adjust crop</button>
              <button onClick={() => patchMedia(m.id, { hidden: !m.hidden })} data-testid={`media-hide-${m.id}`} className={`w-full flex items-center justify-center gap-1.5 text-xs rounded py-1.5 transition-colors ${m.hidden ? "bg-rose text-white hover:bg-rose/90" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
                {m.hidden ? <><Eye size={13} /> Reveal</> : <><EyeOff size={13} /> Hide</>}
              </button>
            </div>
            <button onClick={() => del(m.id)} data-testid={`media-delete-${m.id}`} className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
            </>
            )}
          </div>
        ))}
        {filtered.length === 0 && <p className="text-sm text-stone-400 col-span-full">No photos in this category yet.</p>}
      </div>
    </div>
  );
}
